import type {
  DeviceDiagnostics,
  GameContext,
  PricingOption,
  SessionState,
} from "@teamtable/content-sdk";
import { useEffect, useRef } from "react";

import { trackEvent } from "./analytics/eventQueue.js";
import { useDeviceAuth } from "./auth/DeviceAuthContext.js";
import { mapDeviceDiagnostics } from "./auth/deviceDiagnostics.js";
import { createPhonePresenceSubscriptions } from "./session/phonePresenceSubscriptions.js";
import { usePlaySession } from "./session/PlaySessionContext.js";
import { mapApiPricingOptions, pricingBookFingerprint } from "./session/pricingBook.js";

export function BundleLoader() {
  const { session: deviceSession, diagnostics, reportBundleState } = useDeviceAuth();
  const playSession = usePlaySession();

  const deviceRef = useRef(deviceSession);
  deviceRef.current = deviceSession;
  const contextSubscribers = useRef(new Set<(context: GameContext) => void>());
  const contextFingerprint = useRef<string | undefined>(undefined);
  useEffect(() => {
    const context = deviceSession?.game_context;
    const fingerprint = JSON.stringify(context);
    if (!context || fingerprint === contextFingerprint.current) return;
    contextFingerprint.current = fingerprint;
    for (const cb of contextSubscribers.current) {
      try {
        cb(structuredClone(context));
      } catch (err) {
        console.warn("Context subscriber failed", err);
      }
    }
  }, [deviceSession?.game_context]);

  const stateRef = useRef(playSession.state);
  const sessionRef = useRef(playSession.session);
  const rateRef = useRef(playSession.sessionRate);
  const pricingOptionsRef = useRef(playSession.pricingOptions);
  const pricingScopeRef = useRef(playSession.pricingScope);
  const timeRef = useRef(playSession.timeRemainingSeconds);
  const freePlayRef = useRef(playSession.isFreePlay);
  const freePlayEndsAtRef = useRef(playSession.freePlayEndsAt);
  const phoneOnPayPageRef = useRef(playSession.phoneOnPayPage);
  const subscribersRef = useRef(new Set<(state: SessionState) => void>());
  const pricingSubscribersRef = useRef(new Set<(options: PricingOption[]) => void>());
  const phoneOnPayPageSubscriptionsRef = useRef<ReturnType<
    typeof createPhonePresenceSubscriptions
  > | null>(null);
  phoneOnPayPageSubscriptionsRef.current ??= createPhonePresenceSubscriptions(
    () => phoneOnPayPageRef.current,
  );
  const phoneOnPayPageSubscriptions = phoneOnPayPageSubscriptionsRef.current;
  const pricingFingerprintRef = useRef<string | null>(null);
  const initCalledRef = useRef(false);
  const scriptLoadedRef = useRef(false);
  const diagnosticsRef = useRef(diagnostics);
  const diagnosticsSubscribersRef = useRef(new Set<(value: DeviceDiagnostics | null) => void>());
  const diagnosticsFingerprintRef = useRef<string | undefined>(undefined);
  diagnosticsRef.current = diagnostics;

  useEffect(() => {
    const fingerprint = JSON.stringify(diagnostics);
    if (fingerprint === diagnosticsFingerprintRef.current) return;
    diagnosticsFingerprintRef.current = fingerprint;
    for (const callback of diagnosticsSubscribersRef.current) {
      try {
        callback(mapDeviceDiagnostics(diagnostics));
      } catch (err) {
        console.warn("Content diagnostics subscriber failed", err);
      }
    }
  }, [diagnostics]);

  stateRef.current = playSession.state;
  sessionRef.current = playSession.session;
  rateRef.current = playSession.sessionRate;
  pricingOptionsRef.current = playSession.pricingOptions;
  pricingScopeRef.current = playSession.pricingScope;
  timeRef.current = playSession.timeRemainingSeconds;
  freePlayRef.current = playSession.isFreePlay;
  freePlayEndsAtRef.current = playSession.freePlayEndsAt;
  phoneOnPayPageRef.current = playSession.phoneOnPayPage;

  // Notify subscribers on state change (skip "loading" — bundle never sees it)
  // stateSeq ensures notification fires even when state value is unchanged (e.g. repeated awaiting_payment)
  useEffect(() => {
    if (playSession.state === "loading") return;
    for (const cb of subscribersRef.current) {
      cb(playSession.state as SessionState);
    }
  }, [playSession.state, playSession.stateSeq]);

  // Notify pricing subscribers when the polled book changes
  useEffect(() => {
    if (playSession.state === "loading") return;
    const mapped = mapApiPricingOptions(playSession.pricingOptions);
    const fingerprint = pricingBookFingerprint(mapped, playSession.pricingScope);
    if (fingerprint === pricingFingerprintRef.current) return;
    pricingFingerprintRef.current = fingerprint;
    for (const cb of pricingSubscribersRef.current) {
      cb(mapped);
    }
  }, [playSession.state, playSession.pricingOptions, playSession.pricingScope]);

  // Notify phone-on-pay-page subscribers when the keepalive-derived flag flips
  useEffect(() => {
    if (playSession.state === "loading") return;
    phoneOnPayPageSubscriptions.notify();
  }, [playSession.state, playSession.phoneOnPayPage, phoneOnPayPageSubscriptions]);

  // Build the API object once when device session is available
  const apiRef = useRef<import("@teamtable/content-sdk").TeamtablePlatformAPI | null>(null);
  if (!apiRef.current && deviceSession) {
    apiRef.current = {
      get context() {
        return deviceRef.current?.game_context
          ? structuredClone(deviceRef.current.game_context)
          : undefined;
      },
      subscribeContext: (cb) => {
        contextSubscribers.current.add(cb);
        return () => {
          contextSubscribers.current.delete(cb);
        };
      },
      device: {
        get id() {
          return deviceRef.current?.device_id ?? "";
        },
        get name() {
          return deviceRef.current?.device_name ?? "";
        },
        get venueId() {
          return deviceRef.current?.venue_id ?? null;
        },
        get venueName() {
          return deviceRef.current?.venue_name ?? null;
        },
        getDiagnostics: () => mapDeviceDiagnostics(diagnosticsRef.current),
        subscribeDiagnostics: (cb) => {
          diagnosticsSubscribersRef.current.add(cb);
          return () => {
            diagnosticsSubscribersRef.current.delete(cb);
          };
        },
      },
      session: {
        getState: () => stateRef.current as SessionState,
        getInfo: () => {
          const s = sessionRef.current;
          if (!s) return null;
          return {
            id: s.id,
            verificationCode: s.verification_code,
            verificationPhrase: s.verification_phrase,
            status: s.status,
            startsAt: s.starts_at ?? null,
            endsAt: s.ends_at ?? null,
          };
        },
        getRate: () => {
          const r = rateRef.current;
          if (!r) return null;
          return {
            amountPence: r.amount_pence,
            durationMinutes: r.duration_minutes,
            currency: r.currency,
          };
        },
        getPricingOptions: () => mapApiPricingOptions(pricingOptionsRef.current),
        getPricingScope: () => pricingScopeRef.current,
        getTimeRemaining: () => timeRef.current,
        getPaymentUrl: () => {
          const userPortalUrl = window.serverConfig?.USER_PORTAL_URL || "http://localhost:6177";
          const session = sessionRef.current;
          if (!window.serverConfig?.SESSION_PAYMENT_LINKS_ENABLED) {
            const base = `${userPortalUrl}/pay/${deviceSession.device_id}`;
            if (!session) return base;
            return `${base}?${new URLSearchParams({ paid_session_id: session.id })}`;
          }
          if (!session || !["awaiting_payment", "activating", "active"].includes(session.status)) {
            return null;
          }
          return `${userPortalUrl}/pay/session/${session.id}`;
        },
        isFreePlay: () => freePlayRef.current,
        getFreePlayEndsAt: () => freePlayEndsAtRef.current,
        setInGame: (inGame: boolean) => playSession.setIsInGame(inGame),
        createSession: () => playSession.createSession(),
        endSession: () => playSession.endSession(),
        subscribe: (cb: (state: SessionState) => void) => {
          subscribersRef.current.add(cb);
          return () => {
            subscribersRef.current.delete(cb);
          };
        },
        subscribePricing: (cb: (options: PricingOption[]) => void) => {
          pricingSubscribersRef.current.add(cb);
          return () => {
            pricingSubscribersRef.current.delete(cb);
          };
        },
        isPhoneOnPayPage: () => phoneOnPayPageRef.current,
        subscribePhoneOnPayPage: phoneOnPayPageSubscriptions.subscribe,
      },
      analytics: {
        trackEvent,
      },
    };
  }

  const tryInit = () => {
    if (initCalledRef.current) return;
    if (!scriptLoadedRef.current) return;
    if (!apiRef.current) return;
    if (stateRef.current === "loading") return;

    const bundle = window.teamtableContent;
    if (!bundle) {
      console.error("Content bundle loaded but window.teamtableContent not found");
      return;
    }

    bundle.init(apiRef.current);
    initCalledRef.current = true;
    reportBundleState({ bundle_initialized: true });
  };

  // Precedence: an explicit query param (manual override, and how the kiosk
  // delivers a device's own override) beats the platform setting from the
  // session, which beats the local demo bundle.
  const selectedBundleUrl = useRef<string | null>(null);
  const resolvedBundleUrl = deviceSession
    ? new URLSearchParams(window.location.search).get("content_bundle_url") ||
      deviceSession.content_bundle_url ||
      "http://localhost:6179/demo-content.js"
    : null;
  selectedBundleUrl.current ??= resolvedBundleUrl;
  const bundleUrl = selectedBundleUrl.current;

  // Load the bundle script once the device session names it, with exponential
  // backoff on failure.
  useEffect(() => {
    if (!bundleUrl) return;
    reportBundleState({ bundle_url: bundleUrl, bundle_initialized: false });
    let disposed = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let currentScript: HTMLScriptElement | null = null;

    function loadScript() {
      currentScript?.remove();
      const script = document.createElement("script");
      currentScript = script;
      script.src = bundleUrl as string;
      script.onload = () => {
        if (disposed || currentScript !== script) return;
        scriptLoadedRef.current = true;
        tryInit();
      };
      script.onerror = () => {
        if (disposed) return;
        attempt++;
        const delay = Math.min(5000 * Math.pow(2, attempt - 1), 5 * 60 * 1000);
        console.error(
          `Failed to load content bundle from ${bundleUrl} (attempt ${attempt}, retrying in ${Math.round(delay / 1000)}s)`,
        );
        retryTimer = setTimeout(loadScript, delay);
      };
      document.head.appendChild(script);
    }

    loadScript();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try {
        window.teamtableContent?.destroy?.();
      } finally {
        window.teamtableContent = undefined;
        currentScript?.remove();
        apiRef.current = null;
        scriptLoadedRef.current = false;
        initCalledRef.current = false;
        selectedBundleUrl.current = null;
        contextSubscribers.current.clear();
        diagnosticsSubscribersRef.current.clear();
        subscribersRef.current.clear();
        pricingSubscribersRef.current.clear();
        reportBundleState({ bundle_initialized: false });
      }
    };
  }, [bundleUrl, reportBundleState]);

  // Try init when state transitions away from loading
  useEffect(() => {
    if (playSession.state !== "loading") {
      tryInit();
    }
  }, [playSession.state]);

  return null;
}
